import {
  type Control,
  type ControlValue,
  settingValue,
  valueAttribute,
} from "./control.js";
import { element } from "./dom.js";

/**
 * Building the settings panel's markup.
 *
 * The standard hidden-radio-and-label pattern, styled in
 * `modules/tools.scss`. Bootstrap's `.btn-check` would have done the same job,
 * but the site imports Bootstrap partial by partial and `button-group` is not
 * among them — so using it would mean adding a component to every page on the
 * site for the sake of four.
 */

/**
 * One choice: the radio and the label that covers it.
 */
function choiceNodes(
  control: Control,
  offered: ControlValue,
  settings: object,
  idPrefix: string,
): readonly Node[] {
  const id = `${idPrefix}-${control.field}-${valueAttribute(offered.value)}`;

  const input = element("input", {
    className: "cb-tool-radio",
    attributes: {
      type: "radio",
      name: `${idPrefix}-${control.field}`,
      id,
      value: valueAttribute(offered.value),
      autocomplete: "off",
      ...(settingValue(settings, control.field) === offered.value && {
        checked: "",
      }),
    },
  });

  const label = element("label", {
    className: "cb-tool-choice",
    text: offered.label,
    attributes: { for: id },
  });

  return [input, label];
}

/**
 * One setting: its name, its choices and the sentence explaining it.
 */
export function controlNode(
  control: Control,
  settings: object,
  idPrefix: string,
): Node {
  const group = element("div", {
    className: "cb-tool-choices",
    attributes: { role: "group", "aria-label": control.label },
    children: control.values.flatMap((offered) =>
      choiceNodes(control, offered, settings, idPrefix),
    ),
  });

  return element("fieldset", {
    className: "cb-tool-control",
    attributes: { "data-field": control.field },
    children: [
      element("legend", {
        className: "cb-tool-control-name",
        text: control.label,
      }),
      group,
      element("p", { className: "cb-tool-note", text: control.note }),
    ],
  });
}
